import React from 'react'
import {
  extractAnalyticsText,
  isInternalLink,
  sendClickHash,
  sendClickInternalOrExternal,
  sendSlideSwipe
} from '../../helpers/analytics'
import Icon from './Icon'
import iconExternalLink from '../../images/icons/navigation/open.svg'

function Button({
  children, // Whatever you want to put inside the button
  variant = 'default', // default, icon
  text = '', // Text to display on the button
  theme, // default, primary, secondary
  icon, // The icon to display
  iconSize, // The size of the icon
  iconClasses, // Any extra classes to add to the icon
  link, // The link to go to
  linkTarget, // The target of the link, _blank, _self etc
  size = 'md', // none, sm, md, lg
  classes = '', // Any extra classes to add
  ariaLabel, // The aria label
  onClick, // The click handler
  sendAnalytics = true, // Whether to send analytics or not
  currentScreen = '', // The current screen for analytics purposes
  tabIndex, // The tab index should we need to override it
  isSelected, // If the button is selected
  isDisabled, // If the button is disabled
  currentSlide // The current slide
}) {
  let allClasses = ''

  const isIconOnly = variant === 'icon' && text === ''
  const isIconAndText = variant === 'icon' && text !== ''

  // #region Styles - ..

  // Size is common throughout all variants
  let sizeClasses = ''
  switch (size) {
    case 'none':
      sizeClasses += '' // No size classes
      break
    case 'sm':
      sizeClasses += 'py-1.5 px-6 text-sm'
      break
    case 'lg':
      sizeClasses += 'py-5 px-9 text-lg'
      break
    default:
      sizeClasses += 'py-4 px-5'
      break
  }

  // The defaults
  let baseClasses =
    ' clickable inline-flex justify-center items-center bg-background text-text font-shell-heavy border-2 rounded-lg border-border shadow-fl-btn transition ease-in-out duration-300 m-2 mt-4 gap-[22px] justify-between fill-current'
  // The hover
  baseClasses += ' hover:bg-hover-background hover:text-hover-text hover:border-grey700'
  // The focus
  baseClasses += ' focus:bg-hover-background focus:text-hover-text focus:border-grey700'

  switch (variant) {
    case 'icon':
      // ICON classes. Two possibilities here. Icon only or icon and text
      if (isIconOnly) {
        allClasses += 'p-3'
      } else if (isIconAndText) {
        allClasses += sizeClasses
      }
      break
    default:
      // Standard CTA
      allClasses += sizeClasses
      break
  }

  // Tag on the base classes
  allClasses += baseClasses

  if (isSelected) {
    allClasses += ' border border-grey'
  }
  if (isDisabled) {
    allClasses += ' pe-none text-grey200 cursor-not-allowed'
    tabIndex = -1
  }

  // Add any extras passed through
  allClasses += ' ' + classes

  // #endregion

  // #region Analytics and accessibility

  // Analytics needs a text value
  const analyticsText = extractAnalyticsText(children, text, ariaLabel, 'BUTTON')

  // For the link we need to check if it's got a target set or not
  if (link) {
    if (!linkTarget) {
      // No target specified. Check if it's external or not
      if (isInternalLink(link)) {
        // Internal link
        linkTarget = '_self'
      } else {
        // External link. Open in new tab
        linkTarget = '_blank'
        // Override the icon
        icon = iconExternalLink
      }
    }
  }

  // Use analytics text for aria label if none specified
  if (ariaLabel === '') {
    ariaLabel = analyticsText
  }

  if (linkTarget === '_blank') {
    // Update aria label
    ariaLabel += ' - opens in new window'
  }

  // Model for the icon atom
  const iconModel = {
    icon,
    size: iconSize,
    classes: iconClasses
  }

  // #endregion

  if (!children) {
    children = text
  }

  // Button will fire this when clicked and then pass it up the chain if needed.
  const handleClick = (event, props) => {
    if (sendAnalytics) {
      if (currentSlide) {
        let direction = analyticsText === 'Next slide' ? 'Next' : 'Previous'
        sendSlideSwipe(direction, currentSlide, currentScreen)
      } else if (link) {
        sendClickInternalOrExternal(analyticsText, link, currentScreen)
      } else {
        sendClickHash(analyticsText, '', currentScreen)
      }
    }

    if (onClick) {
      onClick(props)
    }
  }

  if (link) {
    return (
      <a
        tabIndex={tabIndex}
        href={link}
        target={linkTarget}
        data-theme={theme}
        className={allClasses}
        aria-label={ariaLabel}
        onClick={(event) => handleClick(event)}
      >
        {children}
        {icon && <Icon model={iconModel} />}
      </a>
    )
  }

  return (
    <button
      tabIndex={tabIndex}
      type='button'
      data-theme={theme}
      className={allClasses}
      aria-label={ariaLabel}
      onClick={(event) => handleClick(event)}
    >
      {children}
      {icon && <Icon model={iconModel} />}
    </button>
  )
}

export default Button
